import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, FileText, Bookmark, DollarSign, Search, Sparkles,
  ArrowUpRight, Calendar, Clock, Target, Lock
} from "lucide-react";
import StartTrialButton from "@/components/StartTrialButton";

const STATUS_LABELS = {
  draft: "Draft",
  in_review: "In review",
  submitted: "Submitted",
  awarded: "Awarded",
  rejected: "Rejected",
};

const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [matched, setMatched] = useState(null); // null=loading, {grants,needs_profile,locked}

  useEffect(() => {
    api.get("/applications/stats").then((r) => setStats(r.data));
    api.get("/applications").then((r) => setRecent((r.data.applications || []).slice(0, 5)));
    api.get("/grants?limit=6").then((r) => setUpcoming(r.data.grants || []));
    api.get("/grants/matched?limit=5").then((r) => setMatched(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) setMatched({ locked: true });
        else setMatched({ grants: [] });
      });
    // Mount-only fetch; api and setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = stats?.total_applications || 0;
  const requested = stats?.total_requested || 0;
  const awarded = stats?.by_status?.awarded?.count || 0;
  const saved = stats?.saved_grants || 0;

  const renderMatchedContent = () => {
    if (matched === null) {
      return <div className="text-[#5E5E5E] text-sm">Loading matches…</div>;
    }
    if (matched.locked) {
      return (
        <div className="rounded-md bg-[#F9F8F6] border border-dashed border-[#E6E4DF] p-6 text-center">
          <Lock className="w-6 h-6 text-[#5E5E5E] mx-auto"/>
          <p className="text-sm text-[#1A1A1A] mt-3">
            Personalized grant matching is a <b>Pro feature</b>.
          </p>
          <p className="text-xs text-[#5E5E5E] mt-1">Every grant scored by fit to your mission, geography, and budget.</p>
          {user && !user.has_used_trial ? (
            <div className="mt-4 flex flex-col items-center gap-2">
              <StartTrialButton testId="matched-start-trial" />
              <Link to="/pricing" className="text-xs text-[#5E5E5E] hover:text-[#1C3F35] underline">or see all plans</Link>
            </div>
          ) : (
            <Link to="/pricing">
              <button data-testid="matched-upgrade-cta" className="mt-4 bg-[#1C3F35] text-white hover:bg-[#1A362D] px-4 py-2 rounded-md text-sm font-medium">
                See Pro plan
              </button>
            </Link>
          )}
        </div>
      );
    }
    if (matched.needs_profile) {
      return (
        <div className="rounded-md bg-[#F9F8F6] border border-dashed border-[#E6E4DF] p-6 text-center">
          <Target className="w-6 h-6 text-[#1C3F35] mx-auto"/>
          <p className="text-sm text-[#1A1A1A] mt-3">
            Set your organization profile to see matched grants.
          </p>
          <Link to="/settings">
            <button data-testid="matched-profile-cta" className="mt-4 bg-[#1C3F35] text-white hover:bg-[#1A362D] px-4 py-2 rounded-md text-sm font-medium">
              Add profile
            </button>
          </Link>
        </div>
      );
    }
    if (matched.grants?.length) {
      return (
        <ul className="divide-y divide-[#E6E4DF]" data-testid="matched-list">
          {matched.grants.map((g) => (
            <li key={g.id} className="py-3 flex items-start justify-between gap-4" data-testid={`matched-${g.id}`}>
              <div className="min-w-0">
                <Link to={`/grants/${g.id}`} className="font-medium text-[#1A1A1A] hover:text-[#1C3F35] truncate block">
                  {g.title}
                </Link>
                <div className="text-xs text-[#5E5E5E] mt-0.5">
                  {g.agency}
                  {g.match_reasons?.[0] && <span> · {g.match_reasons[0]}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#1C3F35] text-white">
                  {g.match_score}% fit
                </div>
              </div>
            </li>
          ))}
        </ul>
      );
    }
    return <div className="text-[#5E5E5E] text-sm">No matches yet — try broadening your focus areas.</div>;
  };

  return (
    <div className="space-y-10" data-testid="dashboard-root">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Home</div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter text-[#1A1A1A]">
            Welcome back, <span className="italic text-[#1C3F35]">{user?.full_name?.split(" ")[0]}</span>.
          </h1>
          <p className="text-[#5E5E5E] mt-2">Here's the pipeline. Deadlines don't slip on your watch.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/grants">
            <Button data-testid="dashboard-cta-discover" className="bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md">
              <Search className="w-4 h-4 mr-2"/>Discover grants
            </Button>
          </Link>
          <Link to="/ai-writer">
            <Button data-testid="dashboard-cta-ai" variant="outline" className="rounded-md border-[#E6E4DF]">
              <Sparkles className="w-4 h-4 mr-2"/>AI Writer
            </Button>
          </Link>
        </div>
      </header>

      {/* Stat cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
        {[
          { icon: FileText, label: "Applications", value: total, testid: "stat-applications" },
          { icon: DollarSign, label: "Total requested", value: fmtMoney(requested), testid: "stat-requested" },
          { icon: TrendingUp, label: "Awarded", value: awarded, testid: "stat-awarded" },
          { icon: Bookmark, label: "Saved grants", value: saved, testid: "stat-saved" },
        ].map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white border border-[#E6E4DF] rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-md bg-[#E8ECEB] flex items-center justify-center">
                <c.icon className="w-4 h-4 text-[#1C3F35]" strokeWidth={1.75}/>
              </div>
              <span className="label-eyebrow">{c.label}</span>
            </div>
            <div className="mt-4 font-heading text-3xl text-[#1A1A1A] font-medium tracking-tight">{c.value}</div>
          </div>
        ))}
      </section>

      {/* Matched for you */}
      <section className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="matched-section">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#1C3F35]"/>
            <h2 className="font-heading text-xl font-medium">Matched for you</h2>
          </div>
          <Link to="/grants" className="text-sm text-[#1C3F35] flex items-center gap-1 hover:underline">
            All grants <ArrowUpRight className="w-3 h-3"/>
          </Link>
        </div>
        {renderMatchedContent()}
      </section>

      {/* Two-column: recent apps + upcoming grants */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-7 bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="recent-applications">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium">Recent applications</h2>
            <Link to="/applications" className="text-sm text-[#1C3F35] flex items-center gap-1 hover:underline">
              View all <ArrowUpRight className="w-3 h-3"/>
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="py-12 text-center text-[#5E5E5E] text-sm">
              No applications yet. <Link to="/grants" className="text-[#1C3F35] underline">Discover grants</Link> to start.
            </div>
          ) : (
            <ul className="divide-y divide-[#E6E4DF]">
              {recent.map((a) => (
                <li key={a.id} className="py-4 flex items-start justify-between gap-4" data-testid={`recent-app-${a.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A1A1A] truncate">{a.grant_title}</div>
                    <div className="text-xs text-[#5E5E5E] mt-1">{a.grant_agency}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E8ECEB] text-[#1C3F35]">
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                    <div className="text-xs text-[#5E5E5E] mt-1">{fmtMoney(a.amount_requested)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="lg:col-span-5 bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="upcoming-grants">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium">New matches</h2>
            <Link to="/grants" className="text-sm text-[#1C3F35] flex items-center gap-1 hover:underline">
              Explore <ArrowUpRight className="w-3 h-3"/>
            </Link>
          </div>
          <ul className="space-y-4">
            {upcoming.map((g) => {
              const d = daysUntil(g.deadline);
              return (
                <li key={g.id} data-testid={`upcoming-grant-${g.id}`}>
                  <Link to={`/grants/${g.id}`} className="block group">
                    <div className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#1C3F35] transition-colors line-clamp-2">
                      {g.title}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#5E5E5E] mt-1">
                      <span>{g.agency}</span>
                      {g.deadline && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3"/>
                          {d != null && d > 0 ? `${d} days` : "Open"}
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}
